import React from "react";
import { useMutation } from "@apollo/client";
import { MOVE_INTERVIEW_QUESTION } from "graphql/mutations";
import { GET_MISSION_ROLE_INTERVIEW } from "graphql/queries";
import { useQueryData, useToast } from "hooks";
import { FormattedMessage } from "react-intl";
import { buildOrderedRowOptions } from "utils";
import { ICON_TYPE } from "components/Icon";
import Tooltip from "components/Tooltip";

export default function useMissionRoleInterview({ missionRoleId }) {
  const { toast } = useToast();

  const { loading, data, error, refetch } = useQueryData({
    queryName: GET_MISSION_ROLE_INTERVIEW,
    keyName: "missionRoleInterview",
    variables: { missionRoleId },
    skip: !missionRoleId,
  });

  const [moveInterviewQuestion, { loading: moving }] = useMutation(MOVE_INTERVIEW_QUESTION);

  const handleMoveQuestion = (interviewQuestionId, direction, refetchQueries = []) => {
    moveInterviewQuestion({
      variables: {
        input: {
          interviewQuestionId,
          direction,
        },
      },
      refetchQueries,
      onCompleted: () =>
        toast.success(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.MoveQuestionSuccess" />),
      onError: () => toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.MoveQuestionError" />),
    });
  };

  const getRowOptions = ({ question, interviewData, handleEdit, handleDelete }) => {
    if (!question || !interviewData) return [];

    const hasResponses = question?.responsesCount > 0;
    const buildLabel = (labelId) =>
      hasResponses ? (
        <Tooltip tooltipId="useMissionRoleInterview.QuestionHasResponsesTooltip">
          <FormattedMessage id={labelId} />
        </Tooltip>
      ) : (
        <FormattedMessage id={labelId} />
      );

    return [
      {
        label: buildLabel("Global.Edit"),
        icon: ICON_TYPE.edit,
        disabled: hasResponses,
        onClick: () => handleEdit(question),
      },
      {
        label: buildLabel("Global.Remove"),
        icon: ICON_TYPE.trash,
        disabled: hasResponses,
        onClick: () => handleDelete(question),
      },
      ...buildOrderedRowOptions({
        item: question,
        items: interviewData?.questions || [],
        onMove: (id, direction) => handleMoveQuestion(id, direction, [GET_MISSION_ROLE_INTERVIEW]),
      }),
    ];
  };

  return {
    loading,
    error,
    data: data?.missionRoleInterview,
    refetch,
    getRowOptions,
    handleMoveQuestion,
    moving,
  };
}
